import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { GlobalEntityAutocomplete, EntityOption } from './GlobalEntityAutocomplete';
import { RiskRecord } from '../types';

interface RiskAutoCompleteProps {
  projectId: string;
  label: string;
  placeholder?: string;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  currentEntityId?: string;
  currentEntityType?: 'URS' | 'FS' | 'TEST' | 'SOP';
}

export const RiskAutoComplete: React.FC<RiskAutoCompleteProps> = ({
  projectId,
  label,
  placeholder,
  selectedIds,
  onChange,
  currentEntityId,
  currentEntityType
}) => {
  const [options, setOptions] = useState<EntityOption[]>([]);
  const [, setLoading] = useState(true);
  const [readOnlyIds, setReadOnlyIds] = useState<string[]>([]);

  useEffect(() => {
    const loadRisks = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const col = collection(db, 'projects', projectId, 'risks');
        const snapshot = await getDocs(col);
        const loaded: EntityOption[] = snapshot.docs.map(doc => {
          const data = doc.data() as RiskRecord;
          return {
            id: data.id || `RISK-${doc.id.substring(0, 8)}`,
            label: data.title || data.id || `RISK-${doc.id.substring(0, 8)}`,
            title: data.title
          };
        });
        setOptions(loaded);
      } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error loading Risks for autocomplete:', err);
      }
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    loadRisks();
  }, [projectId]);

  // Query for reverse relationships
  useEffect(() => {
    const queryReverseLinks = async () => {
      if (!projectId || !currentEntityId || !currentEntityType) {
        setReadOnlyIds([]);
        return;
      }

      try {
        const reverseIds: string[] = [];
        const riskCol = collection(db, 'projects', projectId, 'risks');
        const riskSnapshot = await getDocs(riskCol);
        riskSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const links = data.links || {};
          
          if (currentEntityType === 'URS' && links.urs_ids?.includes(currentEntityId)) {
            const id = data.id || `RISK-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          } else if (currentEntityType === 'FS' && links.fs_ids?.includes(currentEntityId)) {
            const id = data.id || `RISK-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          } else if (currentEntityType === 'TEST' && links.test_ids?.includes(currentEntityId)) {
            const id = data.id || `RISK-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          } else if (currentEntityType === 'SOP' && links.sop_ids?.includes(currentEntityId)) {
            const id = data.id || `RISK-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          }
        });

        setReadOnlyIds(reverseIds);
      } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error querying reverse links for RISK:', err);
      }
        setReadOnlyIds([]);
      }
    };

    queryReverseLinks();
  }, [projectId, currentEntityId, currentEntityType]);

  return (
    <GlobalEntityAutocomplete
      label={label}
      placeholder={placeholder}
      selectedIds={selectedIds}
      options={options}
      onChange={onChange}
      readOnlyIds={readOnlyIds}
    />
  );
};

